import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Divider, FormControl, Menu, MenuItem } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import pxToRem from "assets/theme/functions/pxToRem";
import { Feature } from "flagged";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import DataTable from "examples/Tables/DataTable";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import CustomButton from "examples/NewDesign/CustomButton";
import DeleteModal from "examples/modal/deleteModal/deleteModal";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";
import ResetFilterButton from "components/Buttons/ResetButton";
import MDBox from "components/MDBox";

import Constants, {
  ButtonTitles,
  Colors,
  Common,
  Icons,
  ModalContent,
  PageTitles,
  DropdownOptions,
  defaultData,
  FeatureTags,
  FiltersModuleName,
} from "utils/Constants";
import { filterProjectStatusFunc } from "utils/methods/methods";
import { projectListThunk } from "redux/Thunks/Filter";
import { locationListByIdThunk } from "redux/Thunks/FieldsData";
import { deleteSnag, exportSnags, getSnags } from "redux/Thunks/Snag";
import { openSnackbar } from "redux/Slice/Notification";

import SnagData from "./data/SnagData";
import SnagViewModal from "./SnagViewModal";
import SnagFormModal from "./SnagFormModal";

const ALL_OPTION = { [Constants.MONGOOSE_ID]: FiltersModuleName.ALL_IN_SMALL_CASE, title: "All" };

const initialFilters = {
  project: FiltersModuleName.ALL_IN_SMALL_CASE,
  location: FiltersModuleName.ALL_IN_SMALL_CASE,
  status: FiltersModuleName.ALL_IN_SMALL_CASE,
};

function SnagList() {
  const dispatch = useDispatch();
  const configState = useSelector((state) => state.config);

  const [snags, setSnags] = useState([]);
  const [allRecordsCount, setAllRecordsCount] = useState(0);
  const [formModal, setFormModal] = useState({ open: false, snag: null });
  const [deleteData, setDeleteData] = useState({ open: false, id: "", loading: false });
  const [viewData, setViewData] = useState({ open: false, snag: null });
  const [loadingStatus, setLoadingStatus] = useState(Constants.PENDING);
  const [tablePagination, setTablePagination] = useState({
    page: 0,
    perPage: defaultData.PER_PAGE,
  });
  const [next, setNext] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  const [projectList, setProjectList] = useState([]);
  const [filterLocationList, setFilterLocationList] = useState([]);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const isMounted = useRef(false);

  const permission = useMemo(() => {
    const screen = configState.screens.find((item) => item.id === defaultData.SNAG_SCREEN_ID);
    return screen?.screensInfo?.agreement || {};
  }, [configState]);

  const showSnackbar = (message, notificationType) => {
    dispatch(openSnackbar({ message, notificationType }));
  };

  const buildListParams = (page, activeFilters = filters) => {
    const params = new URLSearchParams({
      page,
      perPage: tablePagination.perPage,
      sort: "desc",
    });

    if (activeFilters.project !== FiltersModuleName.ALL_IN_SMALL_CASE) {
      params.append("project", activeFilters.project);
    } else {
      const projectStatus = filterProjectStatusFunc();
      if (projectStatus) params.append("projectStatus", projectStatus);
    }
    if (activeFilters.location !== FiltersModuleName.ALL_IN_SMALL_CASE) {
      params.append("location", activeFilters.location);
    }
    if (activeFilters.status !== FiltersModuleName.ALL_IN_SMALL_CASE) {
      params.append("status", activeFilters.status);
    }
    return params;
  };

  const fetchSnags = async (activeFilters = filters) => {
    setLoadingStatus(Constants.PENDING);
    const res = await dispatch(getSnags(buildListParams(0, activeFilters)));
    if (!isMounted.current) return;
    setLoadingStatus(Constants.FULFILLED);
    if (res?.payload?.status === Common.API_STATUS_200) {
      setSnags(res.payload.data?.data?.snagData || []);
      setAllRecordsCount(res.payload.data?.data?.allRecordsCount || 0);
      setNext(0);
      setTablePagination((prev) => ({ ...prev, page: 0 }));
    } else {
      showSnackbar(
        res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
        Constants.NOTIFICATION_ERROR
      );
    }
  };

  const fetchProjects = async () => {
    const res = await dispatch(projectListThunk());
    if (!isMounted.current) return;
    if (res?.payload?.status) {
      setProjectList(Array.isArray(res.payload.data) ? res.payload.data : []);
    }
  };

  const fetchLocations = useCallback(
    async (projectId) => {
      if (!projectId || projectId === FiltersModuleName.ALL_IN_SMALL_CASE) return [];
      const res = await dispatch(locationListByIdThunk({ id: projectId }));
      return Array.isArray(res?.payload?.data) ? res.payload.data : [];
    },
    [dispatch]
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch only once read access is confirmed (a 403 from the API logs the user out)
  useEffect(() => {
    if (permission?.read) {
      fetchSnags();
      fetchProjects();
    }
  }, [permission?.read]);

  /* -------------------------------- Filters ------------------------------- */
  const handleFilterChange = async (e) => {
    const { name, value } = e.target;
    const selectedValue = value || FiltersModuleName.ALL_IN_SMALL_CASE;
    const updatedFilters = {
      ...filters,
      [name]: selectedValue,
      ...(name === "project" && { location: FiltersModuleName.ALL_IN_SMALL_CASE }),
    };
    setFilters(updatedFilters);

    if (name === "project") {
      setFilterLocationList([]);
      const locations = await fetchLocations(selectedValue);
      if (!isMounted.current) return;
      setFilterLocationList(locations);
    }
    await fetchSnags(updatedFilters);
  };

  const handleResetFilter = async () => {
    setFilters(initialFilters);
    setFilterLocationList([]);
    await fetchSnags(initialFilters);
  };

  /* -------------------------------- Export -------------------------------- */
  const handleExport = async (format) => {
    setExportAnchorEl(null);

    if (allRecordsCount === 0) {
      showSnackbar(Constants.SNAG_EXPORT_NO_DATA, Constants.NOTIFICATION_ERROR);
      return;
    }

    // Export uses the same filters as the list
    const params = buildListParams(0);
    params.delete("page");
    params.delete("perPage");
    params.append("format", format === "pdf" ? "pdf" : "excel");

    setExporting(true);
    const res = await dispatch(exportSnags(params));
    if (!isMounted.current) return;
    setExporting(false);

    if (!(res?.payload instanceof Blob)) {
      showSnackbar(
        res?.payload?.message || Constants.SOMETHING_WENT_WRONG,
        Constants.NOTIFICATION_ERROR
      );
      return;
    }

    const currentDate = new Date();
    const filename = `Reynard_snag_list_${currentDate.getFullYear()}-${
      currentDate.getMonth() + 1
    }-${currentDate.getDate()}_${currentDate.getHours()}-${currentDate.getMinutes()}-${currentDate.getSeconds()}.${format}`;

    const url = window.URL.createObjectURL(res.payload);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  /* --------------------------------- Form --------------------------------- */
  const handleOpenFormModal = (snag = null) => setFormModal({ open: true, snag });
  const handleCloseFormModal = () => setFormModal({ open: false, snag: null });

  /* -------------------------------- Delete -------------------------------- */
  const handleOpenDeleteModal = (id) => setDeleteData({ open: true, id, loading: false });
  const handleCloseDeleteModal = () => setDeleteData({ open: false, id: "", loading: false });

  const handleDelete = async () => {
    setDeleteData((prev) => ({ ...prev, loading: true }));
    const res = await dispatch(deleteSnag(deleteData.id));
    if (!isMounted.current) return;
    if (res?.payload?.status === Common.API_STATUS_200) {
      showSnackbar(Constants.SNAG_DELETE_SUCCESS, Constants.NOTIFICATION_SUCCESS);
      handleCloseDeleteModal();
      await fetchSnags();
    } else {
      showSnackbar(
        res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
        Constants.NOTIFICATION_ERROR
      );
      setDeleteData((prev) => ({ ...prev, loading: false }));
    }
  };

  /* ------------------------------ Pagination ------------------------------ */
  const handleCurrentPage = (page) => {
    setTablePagination((prev) => ({ ...prev, page }));
  };

  const handleTablePagination = async () => {
    if (snags.length >= allRecordsCount) return;
    const res = await dispatch(getSnags(buildListParams(next + 1)));
    if (!isMounted.current) return;
    if (res?.payload?.status === Common.API_STATUS_200) {
      const data = res.payload.data?.data?.snagData || [];
      setSnags((prev) => [...prev, ...data]);
      setNext(data.length > 0 ? next + 1 : next);
      setTablePagination((prev) => ({ ...prev, page: next + 1 }));
    }
  };

  const { snagColumns, snagRows } = SnagData(
    snags,
    (snag) => setViewData({ open: true, snag }),
    handleOpenFormModal,
    handleOpenDeleteModal,
    permission
  );

  // Prefill the create form with the project picked in the filter
  const selectedFilterProject =
    projectList.find((project) => project[Constants.MONGOOSE_ID] === filters.project) || null;

  const projectFilterOptions = [ALL_OPTION, ...projectList];
  const locationFilterOptions = [ALL_OPTION, ...filterLocationList];

  const filterConfig = [
    {
      label: FiltersModuleName.PROJECT,
      name: "project",
      options: projectFilterOptions,
    },
    {
      label: FiltersModuleName.LOCATION,
      name: "location",
      options: locationFilterOptions,
    },
    {
      label: FiltersModuleName.STATUS,
      name: "status",
      options: [ALL_OPTION, ...DropdownOptions.SNAG_STATUS_OPTIONS],
    },
  ];

  return (
    <DashboardLayout module={defaultData.SNAG_SCREEN_ID}>
      <DashboardNavbar />
      <MDBox
        display="flex"
        flexDirection={{ md: "row", sm: "column" }}
        justifyContent={{ md: "space-between" }}
        alignItems={{ lg: "space-between", sm: "center" }}
        mt={1}
        mb={2}
      >
        <PageTitle title={PageTitles.SNAG_LIST} subTitle="" />
        <Feature name={FeatureTags.SNAG}>
          <MDBox mt={{ lg: 0, sm: 2 }} display="flex" flexWrap="wrap">
            {permission?.create && (
              <CustomButton
                title={ButtonTitles.SNAG}
                icon={Icons.NEW}
                background={Colors.PRIMARY}
                color={Colors.WHITE}
                openModal={() => handleOpenFormModal()}
              />
            )}
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: "var(--gray-300, #D0D5DD)",
                height: "auto",
                marginLeft: pxToRem(16),
                marginRight: 0,
              }}
            />
            <BasicButton
              title={ButtonTitles.RELOAD}
              icon={Icons.RELOAD}
              background={Colors.WHITE}
              action={() => fetchSnags()}
              border
              color={Colors.BLACK}
            />
          </MDBox>
        </Feature>
      </MDBox>
      <Divider sx={{ marginTop: 1 }} />
      <Feature name={FeatureTags.SNAG}>
        <MDBox
          display="flex"
          flexWrap="wrap"
          justifyContent="start"
          alignItems="flex-end"
          width="100%"
          mb={2}
        >
          {filterConfig.map((filter) => (
            <FormControl
              key={filter.name}
              variant="standard"
              size="medium"
              style={{ marginTop: "15px", width: 200, marginRight: "15px" }}
            >
              <CustomAutoComplete
                label={filter.label}
                name={filter.name}
                id={filter.name}
                getOptionLabel={(option) => option.title || ""}
                menu={filter.options}
                value={{
                  title:
                    filter.options.find(
                      (item) => item[Constants.MONGOOSE_ID] === filters[filter.name]
                    )?.title || "",
                }}
                handleChange={handleFilterChange}
                valueStyle={{
                  backgroundColor: Colors.WHITE,
                  height: pxToRem(40),
                  verticalMarginTop: pxToRem(4),
                  menuWidth: 400,
                  inputWidth: 250,
                  padding: pxToRem(1),
                }}
                labelStyle={{
                  fontSize: pxToRem(14),
                  fontWeight: 600,
                  color: Colors.BLACK,
                }}
                textTransform="none"
              />
            </FormControl>
          ))}
          <ResetFilterButton handleReset={handleResetFilter} style={{ marginLeft: "1rem" }} />
          <Button
            sx={{
              mt: 5.5,
              ml: 1,
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#fff",
              },
              fontSize: pxToRem(14),
              textTransform: "capitalize",
              height: pxToRem(40),
              width: pxToRem(130),
            }}
            variant="outlined"
            color="info"
            onClick={(event) => setExportAnchorEl(event.currentTarget)}
            startIcon={Icons.EXPORT}
            disabled={exporting}
          >
            {exporting ? ButtonTitles.LOADING : ButtonTitles.EXPORT}
          </Button>
          <Menu
            id="snag-export-menu"
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={() => setExportAnchorEl(null)}
          >
            <MenuItem onClick={() => handleExport("xlsx")}>CSV</MenuItem>
            <MenuItem onClick={() => handleExport("pdf")}>PDF</MenuItem>
          </Menu>
        </MDBox>

        <MDBox mt={1} mb={2}>
          <DataTable
            table={{ columns: snagColumns, rows: snagRows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: defaultData.PER_PAGE }}
            showTotalEntries={false}
            noEndBorder
            loading={loadingStatus}
            currentPage={tablePagination.page}
            handleTablePagination={handleTablePagination}
            handleCurrentPage={handleCurrentPage}
          />
        </MDBox>

        <SnagFormModal
          open={formModal.open}
          snag={formModal.snag}
          projectList={projectList}
          defaultProject={selectedFilterProject}
          handleClose={handleCloseFormModal}
          onSaved={() => fetchSnags()}
        />
      </Feature>

      <DeleteModal
        open={deleteData.open}
        title={ModalContent.DELETE_SNAG_TITLE}
        message={ModalContent.DELETE_SNAG_MESSAGE}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
        loading={deleteData.loading}
      />

      {viewData.open && (
        <SnagViewModal
          open={viewData.open}
          snag={viewData.snag}
          handleClose={() => setViewData({ open: false, snag: null })}
        />
      )}
    </DashboardLayout>
  );
}

export default SnagList;
